import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";
import { BULAN, TAHUN_AWAL } from "./config/appConfig";
import { RAW_HOUSES } from "./data/houses";

// Batas tunggakan: di atas 3 bulan (4 bulan atau lebih) baru ditampilkan.
// Telat 1 dan 2 bulan tidak dihitung.
const BATAS_TUNGGAKAN_BULAN = 3;

function getMonthIndexFromName(bulanName) {
  if (bulanName === null || bulanName === undefined) return -1;

  const s = String(bulanName).trim().toLowerCase();
  if (!s) return -1;

  // 1. Exact match against BULAN array (Indonesian full names)
  const exactIdx = BULAN.findIndex((b) => b.toLowerCase() === s);
  if (exactIdx !== -1) return exactIdx;

  // 2. Numeric month (1-12 or "01"-"12")
  const num = parseInt(s, 10);
  if (!isNaN(num) && num >= 1 && num <= 12) return num - 1;

  // 3. Alternative Indonesian spellings & common abbreviations
  //    yang tidak tertangkap oleh prefix match di langkah 5
  const altSpellings = {
    pebruari: 1, peb: 1,
    nopember: 10, nop: 10,
    agt: 7,
  };
  if (s in altSpellings) return altSpellings[s];

  // 4. English full month names
  const englishMonths = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december"
  ];
  const enIdx = englishMonths.findIndex((m) => m === s);
  if (enIdx !== -1) return enIdx;

  // 5. Prefix/abbreviation match (minimum 3 characters)
  //    e.g., "nov" → November, "sep" → September, "jan" → Januari
  if (s.length >= 3) {
    const prefixIdx = BULAN.findIndex((b) => b.toLowerCase().startsWith(s));
    if (prefixIdx !== -1) return prefixIdx;

    const enPrefixIdx = englishMonths.findIndex((m) => m.startsWith(s));
    if (enPrefixIdx !== -1) return enPrefixIdx;
  }

  return -1;
}

// Status yang dianggap lunas: "Lunas" dan "Sudah verifikasi"
// (keduanya berwarna hijau di StatusBadge, berarti pembayaran sah/terverifikasi).
// Status NULL/kosong di Supabase berarti belum bayar, tidak dihitung sebagai lunas.
function isStatusLunas(status) {
  const s = String(status || "").toLowerCase().trim();
  if (!s) return false;
  // Eksklusi status negatif (jangan dihitung sebagai lunas).
  // Penting: "belum lunas" mengandung kata "lunas", jadi harus dikeluarkan dulu.
  if (
    s.includes("belum") ||
    s.includes("menunggu") ||
    s.includes("pending") ||
    s.includes("ditolak") ||
    s.includes("gagal") ||
    s.includes("batal")
  ) {
    return false;
  }
  // Status positif yang dianggap lunas/terverifikasi
  return (
    s.includes("lunas") ||
    s.includes("verifikasi") ||
    s.includes("verified") ||
    s.includes("valid") ||
    s.includes("sudah bayar") ||
    s.includes("dibayar")
  );
}

// Ambil SEMUA data pembayaran dari Supabase dengan pagination.
// Supabase default hanya mengembalikan 1000 baris, jadi untuk rumah ~170 x
// pembayaran multi-tahun, banyak record tidak terambil. Kita loop halaman.
async function fetchAllPembayaran() {
  const PAGE_SIZE = 1000;
  let allData = [];
  let from = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await supabase
      .from("pembayaran")
      .select("nomor_rumah, bulan, tahun, status")
      .range(from, from + PAGE_SIZE - 1);

    if (error) throw error;

    if (!data || data.length === 0) break;

    allData = allData.concat(data);

    // Jika halaman ini kurang dari PAGE_SIZE, berarti sudah halaman terakhir
    if (data.length < PAGE_SIZE) break;

    from += PAGE_SIZE;
  }

  return allData;
}

export default function LaporanTunggakanTab() {
  const [tunggakanList, setTunggakanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const muatDataTunggakan = useCallback(async () => {
    try {
      setIsRefreshing(true);
      setError("");

      // Ambil semua data pembayaran dari Supabase (dengan pagination)
      const pembayaran = await fetchAllPembayaran();
      console.log(
        "[Tunggakan] Total record pembayaran terambil:",
        (pembayaran || []).length
      );

      // Debug: hitung distribusi status untuk memudahkan troubleshooting
      const statusCount = {};
      for (const p of pembayaran || []) {
        const s = String(p.status || "(kosong)").trim();
        statusCount[s] = (statusCount[s] || 0) + 1;
      }
      console.log("[Tunggakan] Distribusi status pembayaran:", statusCount);

      // Tanggal sistem untuk perhitungan otomatis
      const now = new Date();
      const currentAbsolute = now.getFullYear() * 12 + now.getMonth();

      // Titik awal tunggakan: bulan pertama di TAHUN_AWAL (Januari TAHUN_AWAL).
      // Rumah yang belum pernah membayar sama sekali dihitung menunggak sejak
      // awal periode IPL berlaku, bukan dilewati.
      const startAbsolute = TAHUN_AWAL * 12 + 0; // Januari TAHUN_AWAL

      const list = [];

      for (const house of RAW_HOUSES) {
        const houseNo = String(house.nomor_rumah || "").trim().toUpperCase();

        // Ambil semua pembayaran yang statusnya lunas/terverifikasi
        const lunasRecords = (pembayaran || []).filter(
          (p) =>
            String(p.nomor_rumah || "").trim().toUpperCase() === houseNo &&
            isStatusLunas(p.status)
        );

        // Debug: tampilkan record non-lunas untuk rumah ini (membantu
        // identifikasi pembayaran yang statusnya belum diverifikasi admin)
        const nonLunasRecords = (pembayaran || []).filter(
          (p) =>
            String(p.nomor_rumah || "").trim().toUpperCase() === houseNo &&
            !isStatusLunas(p.status)
        );
        if (nonLunasRecords.length > 0) {
          console.log(
            `[Tunggakan] ${houseNo}: ${nonLunasRecords.length} record non-lunas (dilewati):`,
            nonLunasRecords.map((r) => ({
              bulan: r.bulan,
              tahun: r.tahun,
              status: r.status,
            }))
          );
        }

        // Cari pembayaran terakhir yang berstatus lunas/terverifikasi.
        // Status NULL di Supabase dianggap belum bayar, sehingga tidak
        // masuk ke lunasRecords dan tidak memajukan tanggal pembayaran
        // terakhir.
        let lastPaidAbsolute = null;
        let lastPaidLabel = "Belum pernah membayar";

        // Debug: tampilkan semua record lunas untuk rumah ini
        if (lunasRecords.length > 0) {
          console.log(
            `[Tunggakan] ${houseNo}: ${lunasRecords.length} record lunas:`,
            lunasRecords.map((r) => ({
              bulan: r.bulan,
              tahun: r.tahun,
              status: r.status,
            }))
          );
        }

        for (const rec of lunasRecords) {
          const monthIdx = getMonthIndexFromName(rec.bulan);
          const year = parseInt(rec.tahun);

          if (monthIdx === -1) {
            console.warn(
              `[Tunggakan] ⚠️ ${houseNo}: record dilewati — bulan "${rec.bulan}" tidak dikenali (tahun: ${rec.tahun}, status: ${rec.status})`
            );
            continue;
          }
          if (isNaN(year)) {
            console.warn(
              `[Tunggakan] ⚠️ ${houseNo}: record dilewati — tahun "${rec.tahun}" tidak valid (bulan: ${rec.bulan}, status: ${rec.status})`
            );
            continue;
          }

          const abs = year * 12 + monthIdx;
          if (lastPaidAbsolute === null || abs > lastPaidAbsolute) {
            lastPaidAbsolute = abs;
            lastPaidLabel = `${BULAN[monthIdx]} ${year}`;
          }
        }

        // Hitung tunggakan dari terakhir kali pembayaran sampai bulan
        // saat ini. Jika belum pernah membayar sama sekali, hitung dari
        // awal periode IPL (Januari TAHUN_AWAL).
        let unpaidCount = 0;
        if (lastPaidAbsolute !== null) {
          // Sudah pernah bayar: hitung bulan setelah pembayaran terakhir
          unpaidCount = currentAbsolute - lastPaidAbsolute;
          if (unpaidCount < 0) unpaidCount = 0;
        } else {
          // Belum pernah bayar: hitung dari awal periode IPL sampai sekarang
          unpaidCount = currentAbsolute - startAbsolute + 1;
          if (unpaidCount < 0) unpaidCount = 0;
        }

        // Hanya tampilkan jika tunggakan di atas batas (lebih dari 3 bulan)
        if (unpaidCount > BATAS_TUNGGAKAN_BULAN) {
          console.log(
            `[Tunggakan] ${houseNo}: terakhir bayar=${lastPaidLabel} (abs=${lastPaidAbsolute}), currentAbs=${currentAbsolute}, tunggakan=${unpaidCount} bulan`
          );
          list.push({
            nomor_rumah: house.nomor_rumah,
            nama: house.nama,
            terakhir_bayar: lastPaidLabel,
            bulan_tunggakan: unpaidCount
          });
        }
      }

      // Urutkan berdasarkan jumlah bulan tunggakan terbesar
      list.sort((a, b) => b.bulan_tunggakan - a.bulan_tunggakan);
      console.log(
        "[Tunggakan] Total rumah diproses:",
        RAW_HOUSES.length,
        "| Menunggak:",
        list.length
      );
      setTunggakanList(list);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Gagal memuat data tunggakan:", err);
      setError(
        "Gagal memuat data tunggakan: " +
          (err.message || String(err)) +
          "\n\nPastikan kebijakan RLS Supabase mengizinkan pembacaan tabel pembayaran."
      );
    } finally {
      setIsRefreshing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    muatDataTunggakan();

    // Subscribe to real-time updates untuk auto-refresh saat ada perubahan pembayaran
    const channel = supabase
      .channel("pembayaran-tunggakan")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "pembayaran" },
        () => {
          muatDataTunggakan();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [muatDataTunggakan]);

  const totalBulanTunggakan = tunggakanList.reduce(
    (sum, item) => sum + item.bulan_tunggakan,
    0
  );

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#8A8F86" }}>
        Memuat data tunggakan...
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          background: "#1E5631",
          color: "#fff",
          borderRadius: "14px",
          padding: "20px",
          marginBottom: "18px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
        }}
      >
        <div style={{ fontSize: "20px", fontWeight: "bold" }}>
          ⚠️ Laporan Tunggakan
        </div>
        <div style={{ fontSize: "14px", opacity: 0.9, marginTop: "6px" }}>
          Per {lastUpdate.toLocaleDateString("id-ID", {
            day: "numeric",
            month: "long",
            year: "numeric"
          })}{" "}
          ·{" "}
          {lastUpdate.toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit"
          })}{" "}
          WIB
        </div>
        <div style={{ fontSize: "13px", opacity: 0.85, marginTop: "4px" }}>
          Menampilkan rumah dengan tunggakan di atas {BATAS_TUNGGAKAN_BULAN}{" "}
          bulan (telat 1-2 bulan tidak dihitung)
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "18px" }}>
        <div
          style={{
            flex: 1,
            background: "#fff",
            borderRadius: "12px",
            padding: "16px",
            textAlign: "center",
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)"
          }}
        >
          <div style={{ fontSize: "13px", color: "#5A6059" }}>Total Rumah</div>
          <div
            style={{
              fontSize: "28px",
              fontWeight: "bold",
              color: "#C24C4C",
              marginTop: "4px"
            }}
          >
            {tunggakanList.length}
          </div>
        </div>
        <div
          style={{
            flex: 1,
            background: "#fff",
            borderRadius: "12px",
            padding: "16px",
            textAlign: "center",
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)"
          }}
        >
          <div style={{ fontSize: "13px", color: "#5A6059" }}>
            Total Bulan Tunggakan
          </div>
          <div
            style={{
              fontSize: "28px",
              fontWeight: "bold",
              color: "#C24C4C",
              marginTop: "4px"
            }}
          >
            {totalBulanTunggakan}
          </div>
        </div>
      </div>

      {/* Refresh Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "12px"
        }}
      >
        <button
          type="button"
          onClick={muatDataTunggakan}
          disabled={isRefreshing}
          style={{
            background: "#1E5631",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            padding: "8px 14px",
            fontSize: "13px",
            fontWeight: "bold",
            cursor: isRefreshing ? "not-allowed" : "pointer",
            opacity: isRefreshing ? 0.7 : 1
          }}
        >
          {isRefreshing ? "Memuat..." : "🔄 Refresh"}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div
          style={{
            background: "#FDF2F2",
            color: "#C24C4C",
            padding: "14px",
            borderRadius: "10px",
            marginBottom: "14px",
            fontSize: "14px",
            whiteSpace: "pre-wrap",
            borderLeft: "5px solid #C24C4C"
          }}
        >
          {error}
        </div>
      )}

      {/* Table */}
      {!error && tunggakanList.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            color: "#8A8F86",
            padding: "40px",
            background: "#fff",
            borderRadius: "12px",
            fontSize: "15px"
          }}
        >
          ✅ Tidak ada rumah dengan tunggakan di atas{" "}
          {BATAS_TUNGGAKAN_BULAN} bulan.
        </div>
      ) : (
        !error && (
          <div
            style={{
              overflowX: "auto",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              WebkitOverflowScrolling: "touch"
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "#fff",
                minWidth: "100%",
                tableLayout: "fixed"
              }}
            >
              <thead>
                <tr style={{ background: "#1E5631", color: "#fff" }}>
                  <th
                    style={{
                      padding: "14px 10px",
                      textAlign: "left",
                      fontSize: "14px",
                      fontWeight: "bold",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.3,
                      verticalAlign: "top",
                      width: "30%",
                      maxWidth: "120px"
                    }}
                  >
                    Nomor rumah
                  </th>
                  <th
                    style={{
                      padding: "14px 10px",
                      textAlign: "left",
                      fontSize: "14px",
                      fontWeight: "bold",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.3,
                      verticalAlign: "top",
                      width: "35%"
                    }}
                  >
                    Terakhir membayar
                  </th>
                  <th
                    style={{
                      padding: "14px 10px",
                      textAlign: "right",
                      fontSize: "14px",
                      fontWeight: "bold",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.3,
                      verticalAlign: "top",
                      width: "35%"
                    }}
                  >
                    Bulan Tunggakan
                  </th>
                </tr>
              </thead>
              <tbody>
                {tunggakanList.map((item, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: "1px solid #EAEBE6",
                      background: idx % 2 === 0 ? "#fff" : "#F8FAF6"
                    }}
                  >
                    <td
                      style={{
                        padding: "12px 10px",
                        fontSize: "15px",
                        fontWeight: "bold",
                        color: "#1E2A22"
                      }}
                    >
                      {item.nomor_rumah}
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "normal",
                          color: "#8A8F86",
                          marginTop: "2px"
                        }}
                      >
                        {item.nama}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "12px 10px",
                        fontSize: "14px",
                        color: "#5A6059"
                      }}
                    >
                      {item.terakhir_bayar}
                    </td>
                    <td
                      style={{
                        padding: "12px 10px",
                        textAlign: "right"
                      }}
                    >
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: "bold",
                          color: "#C24C4C"
                        }}
                      >
                        {item.bulan_tunggakan} bulan
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Info Box */}
      <div
        style={{
          background: "#F8FAF3",
          padding: "14px",
          borderRadius: "10px",
          marginTop: "16px",
          fontSize: "13px",
          color: "#5A6059",
          borderLeft: "5px solid #1E5631",
          lineHeight: 1.5
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: "6px", color: "#1E5631" }}>
          ℹ️ Keterangan:
        </div>
        <div>
          • Perhitungan otomatis berdasarkan tanggal sistem (real time).
        </div>
        <div>
          • Status pembayaran mengikuti hasil verifikasi Admin (status "Lunas"
          dan "Sudah verifikasi" dihitung).
        </div>
        <div>
          • Rumah dengan tunggakan 1-2 bulan tidak ditampilkan.
        </div>
        <div>
          • Rumah yang belum pernah membayar dihitung menunggak sejak awal
          periode IPL ({TAHUN_AWAL}).
        </div>
        <div>
          • Tunggakan dihitung dari terakhir kali pembayaran. Status
          pembayaran NULL di Supabase berarti belum bayar.
        </div>
      </div>
    </div>
  );
}